import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  Tag,
  RefreshCw
} from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { formatDateTime } from '../../utils/formatDateTime';
import { useSettings } from '../../contexts/SettingsContext';
import Button from '../../components/UI/Button';
import { DeletionProtectionModal } from '../../components/UI';
import { useDeletionProtection } from '../../hooks/useDeletionProtection';
import { getBrandLogoUrl } from '../../utils/logoUtils';

interface Brand {
  _id: string;
  name: string;
  description?: string;
  website?: string;
  logo?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const BrandsPage: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [newBrand, setNewBrand] = useState({
    name: '',
    description: '',
    website: '',
    isActive: true,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const { settings } = useSettings();

  // Deletion protection
  const {
    showDeletionProtectionModal,
    deletionProtectionData,
    checkAndDelete,
    closeDeletionProtectionModal
  } = useDeletionProtection();

  useEffect(() => {
    fetchBrands();
  }, []);

  // Soft refresh for brands
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateMasterDataCache();
      fetchBrands();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/brands?limit=1000'); // Request all brands
      setBrands(response.data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
      toast.error('Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (brandId: string) => {
    if (!logoFile) return;

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('logo', logoFile);
      await apiService.post(`/brands/${brandId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLogoFile(null);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleAddBrand = async () => {
    try {
      const response = await apiService.post('/brands', newBrand);
      const createdBrand = response.data;
      
      if (logoFile) {
        await handleLogoUpload(createdBrand._id);
      }

      toast.success('Brand added successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowAddModal(false);
      setNewBrand({
        name: '',
        description: '',
        website: '',
        isActive: true,
      });
      setLogoFile(null);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchBrands();
      }, 100);
    } catch (error) {
      toast.error('Failed to add brand');
    }
  };

  const handleEditBrand = async () => {
    if (!editingBrand) return;
    
    try {
      await apiService.put(`/brands/${editingBrand._id}`, editingBrand);
      
      if (logoFile) {
        await handleLogoUpload(editingBrand._id);
      }

      toast.success('Brand updated successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowEditModal(false);
      setEditingBrand(null);
      setLogoFile(null);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchBrands();
      }, 100);
    } catch (error) {
      toast.error('Failed to update brand');
    }
  };

  const handleDeleteBrand = async (brandId: string) => {
    const brand = brands.find(b => b._id === brandId);
    if (!brand) return;

    const deleteFunction = async () => {
      try {
        const response = await apiService.delete(`/brands/${brandId}`);
        toast.success('Brand deleted successfully');
        
        // Immediately clear master data cache and force refresh
        apiService.invalidateMasterDataCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchBrands();
        }, 100);
      } catch (error) {
        console.error('Error deleting brand:', error);
        toast.error('Failed to delete brand');
      }
    };

    await checkAndDelete('brands', brandId, brand.name, deleteFunction);
  };

  const openEditModal = (brand: Brand) => {
    setEditingBrand(brand);
    setShowEditModal(true);
  };

  const filteredBrands = brands.filter(brand => {
    const matchesSearch = brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         brand.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || brand.isActive === (statusFilter === 'active');
    
    return matchesSearch && matchesStatus;
  });

  const exportBrands = async () => {
    try {
      await apiService.download('/brands/export/excel', 'brands.xlsx');
      toast.success('Brands exported successfully');
    } catch (error) {
      console.error('Error exporting brands:', error);
      toast.error('Failed to export brands');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6 font-[var(--font-family)] text-[var(--color-text)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>Brands</h1>
          <p style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>
            Manage your product brands
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => { 
              apiService.invalidateMasterDataCache(); 
              fetchBrands(); 
            }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            Add Brand
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search brands..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
          <button
            onClick={exportBrands}
            className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 pt-4 border-t border-[var(--color-border)]"
          >
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Brands Table */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm overflow-hidden" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <DoubleScrollbar>
          <table className="min-w-full divide-y divide-[var(--color-border)]">
            <thead className="bg-[var(--color-surface-light)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Website
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
              {filteredBrands.map((brand) => {
                const logoUrl = getBrandLogoUrl(brand.name, brand.logo);
                return (
                  <tr key={brand._id} className="hover:bg-[var(--color-surface-light)]">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {logoUrl ? (
                          <img 
                            src={logoUrl} 
                            alt={brand.name}
                            className="w-10 h-10 object-contain mr-2 rounded border border-[var(--color-border)] bg-white"
                          />
                        ) : (
                          <Tag className="w-10 h-10 text-[var(--color-primary)] mr-2 p-2 border border-[var(--color-border)] rounded bg-[var(--color-surface-light)]" />
                        )}
                        <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                          {brand.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                      {brand.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                      {brand.website || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-['Times_New_Roman']">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-['Times_New_Roman'] ${brand.isActive ? 'bg-[var(--color-success-light)] text-[var(--color-success-dark)]' : 'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)]'}`}>{brand.isActive ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{formatDateTime(brand.createdAt, settings)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openEditModal(brand)}
                          className="text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-['Times_New_Roman']"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteBrand(brand._id)}
                          className="text-[var(--color-danger)] hover:text-[var(--color-danger-dark)] font-['Times_New_Roman']"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DoubleScrollbar>
        {/* Empty State */}
        {filteredBrands.length === 0 && (
          <div className="text-center py-12">
            <Tag className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
              No brands found
            </h3>
            <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              {searchTerm || statusFilter ? 'Try adjusting your search or filters.' : 'Get started by creating your first brand.'}
            </p>
          </div>
        )}
      </div>
      {/* Add Brand Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setLogoFile(null);
        }}
        title="Add New Brand"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newBrand.name}
            onChange={e => setNewBrand({ ...newBrand, name: e.target.value })}
            placeholder="Enter brand name"
            required
          />
          <Input
            label="Description"
            value={newBrand.description}
            onChange={e => setNewBrand({ ...newBrand, description: e.target.value })}
            placeholder="Enter brand description"
          />
          <Input
            label="Website"
            value={newBrand.website}
            onChange={e => setNewBrand({ ...newBrand, website: e.target.value })}
            placeholder="Enter brand website"
          />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
              Brand Logo
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={e => setLogoFile(e.target.files ? e.target.files[0] : null)}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            />
            {logoFile && (
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                Selected: {logoFile.name}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
              Status
            </label>
            <select
              value={newBrand.isActive ? 'active' : 'inactive'}
              onChange={e => setNewBrand({ ...newBrand, isActive: e.target.value === 'active' })}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => {
              setShowAddModal(false);
              setLogoFile(null);
            }}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleAddBrand}
            disabled={uploadingLogo}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman'] disabled:opacity-50"
          >
            {uploadingLogo ? 'Uploading...' : 'Add Brand'}
          </button>
        </div>
      </Modal>
      {/* Edit Brand Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setLogoFile(null);
        }}
        title="Edit Brand"
      >
        {editingBrand && (
          <div className="space-y-4">
            <Input
              label="Name"
              value={editingBrand.name}
              onChange={e => setEditingBrand({ ...editingBrand, name: e.target.value })}
              placeholder="Enter brand name"
              required
            />
            <Input
              label="Description"
              value={editingBrand.description || ''}
              onChange={e => setEditingBrand({ ...editingBrand, description: e.target.value })}
              placeholder="Enter brand description"
            />
            <Input
              label="Website"
              value={editingBrand.website || ''}
              onChange={e => setEditingBrand({ ...editingBrand, website: e.target.value })}
              placeholder="Enter brand website"
            />
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                Brand Logo
              </label>
              {(editingBrand.logo || getBrandLogoUrl(editingBrand.name)) && !logoFile && (
                <div className="mb-2">
                  <img 
                    src={getBrandLogoUrl(editingBrand.name, editingBrand.logo)} 
                    alt="Current Logo" 
                    className="w-20 h-20 object-contain border border-[var(--color-border)] rounded bg-white"
                  />
                  <p className="text-xs text-[var(--color-text-secondary)] mt-1">Current logo</p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={e => setLogoFile(e.target.files ? e.target.files[0] : null)}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
              {logoFile && (
                <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                  Selected to upload: {logoFile.name}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                Status
              </label>
              <select
                value={editingBrand.isActive ? 'active' : 'inactive'}
                onChange={e => setEditingBrand({ ...editingBrand, isActive: e.target.value === 'active' })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => {
              setShowEditModal(false);
              setLogoFile(null);
            }}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleEditBrand}
            disabled={uploadingLogo}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman'] disabled:opacity-50"
          >
            {uploadingLogo ? 'Updating...' : 'Update Brand'}
          </button>
        </div>
      </Modal>

      {/* Deletion Protection Modal */}
      <DeletionProtectionModal
        isOpen={showDeletionProtectionModal}
        onClose={closeDeletionProtectionModal}
        entityName={deletionProtectionData?.entityName || ''}
        entityType={deletionProtectionData?.entityType || ''}
        dependencies={deletionProtectionData?.dependencies || []}
        onViewDetails={(dependency) => {
          // Navigate to the dependency details
          console.log('View details for:', dependency);
          // You can implement navigation here if needed
        }}
      />
    </div>
  );
};

export default BrandsPage; 